import { FC, useState, useEffect } from 'react'
import clsx from 'clsx'
import { useRouter } from 'next/router'

import WebinarKeyMoments from './webinar-key-moments/webinar-key-moments'
import WebinarListen from './webinar-listen/webinar-listen'
import WebinarRead from './webinar-read/webinar-read'
import SwitchToggleDropdown from 'components/parts/switch-toggle-dropdown/switch-toggle-dropdown'
import useLectorsSubscription from 'hooks/useLectorsSubscription'
import useSearchSelectedOption from 'hooks/useSearchSelectedOption'
import Commentaries from 'components/parts/commentaries/commentaries'
import WidthLimiter from 'containers/width-limiter/width-limiter'
import VideoBlock from '../video-block/video-block'
import WriteUsBlock from 'components/parts/write-us/write-us-block'

import { getAxiosSingle } from 'pages/api/axios'
import { URL_LIST } from 'constants/url'
import { IMaterial } from 'constants/types/webinar.types'
import { Option, OptionAliases, Options, specWebinarAnchors } from './data'

import styles from 'components/articles/articles.module.scss'
import stylesFormatWebinar from './webinar-with-format.module.scss'
import stylesVideo from 'components/articles/article-video/article-video.module.scss'

interface WebinarWithFormat {
  webinarData: IMaterial
}

const WebinarWithFormat: FC<WebinarWithFormat> = ({ webinarData }) => {
  const { push } = useRouter()
  const [dibabledOptions, setDibabledOptions] = useState(null)
  const lectorsData = useLectorsSubscription(webinarData.Lecturs, '')

  const { selectedOption, setSelectedOption } = useSearchSelectedOption(OptionAliases)

  const isWebinar = selectedOption === Option.watch
  const isKeyMoments = selectedOption === Option.keyMoments
  const isListen = selectedOption === Option.listen
  const isRead = selectedOption === Option.read

  const Comments = () => (
    <>
      <Commentaries
        comments={webinarData.Comments}
        id={webinarData.id}
        materialType={isWebinar ? 'webinar' : 'article'}
        isWithNMO={Boolean(webinarData.activeNMOCodes)}
        isAllowComment={webinarData?.isAcceptAllow}
      />
      <WriteUsBlock />
    </>
  )

  const getAnchor = () => {
    const currentUrl = new URL(window.location.href)
    if (!currentUrl) return

    const path = `${currentUrl.origin}${currentUrl.pathname}${currentUrl.search}`

    if (isWebinar) push(`${path}#${specWebinarAnchors.WATCH}`)
    if (isRead) push(`${path}#${specWebinarAnchors.READ}`)
    if (isListen) push(`${path}#${specWebinarAnchors.LISTEN}`)
    if (isKeyMoments) push(`${path}#${specWebinarAnchors.KEY_MOMENTS}`)
  }

  useEffect(() => {
    const getDisabledOptions = async () => {
      const result = await getAxiosSingle(
        `${URL_LIST.FORMAT_WEBINAR_ACTIVE_OPTIONS}/${webinarData.id}`
      )
      setDibabledOptions(result)
    }
    getDisabledOptions()
  }, [])

  useEffect(() => {
    getAnchor()
  }, [selectedOption])

  return (
    <div className={stylesVideo.articleVideo}>
      <div className={styles.articleWrapper}>
        <div className={clsx(stylesVideo.articleTopVideo, stylesVideo.wrapperWithFormat)}>
          <section
            className={clsx(styles.recomendationSection, styles.recomendationSectionVertical)}
          >
            <WidthLimiter
              noTopPadding
              noBottomPadding
              noLeftPadding
              noRightPadding
              modificator={styles.modificatorWidth}
            >
              <SwitchToggleDropdown
                options={Options}
                selectedOption={selectedOption}
                handleToggle={setSelectedOption}
                disabledOptions={dibabledOptions}
              />
            </WidthLimiter>
          </section>
          <WidthLimiter
            noTopPadding
            noBottomPadding
            noLeftPadding
            noRightPadding
            modificator={stylesFormatWebinar.widthLimMod}
          >
            <div className={stylesVideo.whiteBgVideo}>
              <main>
                {isWebinar && <VideoBlock dataset={webinarData} lectorsData={lectorsData} />}
                {isKeyMoments && (
                  <WebinarKeyMoments
                    dataset={webinarData}
                    lectorsData={lectorsData}
                    commentsAmmount={1}
                  />
                )}
                {isListen && <WebinarListen dataset={webinarData} />}
                {isRead && <WebinarRead dataset={webinarData} commentsAmmount={1} />}
              </main>
            </div>

            <div className={styles.commentariesTop}>
              <Comments />
            </div>
          </WidthLimiter>
        </div>
      </div>
      <div className={styles.commentariesBottom}>
        <WidthLimiter noTopPadding noBottomPadding>
          <Comments />
        </WidthLimiter>
      </div>
    </div>
  )
}

export default WebinarWithFormat
